import { ErrorHandler, Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BaseDataService } from '../base-data/base-data.service';
import { Secure } from '../../enums/secure.enum';
import { ErrorDialogService } from '../../dialogs/error/error-dialog.service';
import { ClientErrorReport } from '../../models/error/client-error-report.model';
import { LeathermanAppConfigInjectionToken } from '../../config/injection-tokens/leatherman-app-config.injection-token';
import { ILeathermanAppConfig } from '../../config/app.config';
import { ErrorDialogServiceInjectionToken } from '../../config/injection-tokens/error-dialog-service.injection-token';

/**
 * A custom error handler service for unhandled exceptions
 */
@Injectable({
  providedIn: 'root'
})
export class CustomErrorHandlerService extends BaseDataService
  implements ErrorHandler {
  /**
   * Constructor
   * @param config - The app config object
   * @param errorDialogService - A reference to the error dialog service
   * @param router - A reference to the router
   * @param httpClient - A reference to the HTTP client
   */
  constructor(
    @Inject(LeathermanAppConfigInjectionToken) config: ILeathermanAppConfig,
    @Inject(ErrorDialogServiceInjectionToken)
    errorDialogService: ErrorDialogService,
    router: Router,
    httpClient: HttpClient
  ) {
    super(config, errorDialogService, router, httpClient);
    this.baseEndpoint = 'error/client';
  }

  /**
   * Delete a client error from the database
   * @param clientErrorId - The ID of the client error to delete
   * @returns True if the delete suceeded
   * @async
   */
  public async deleteClientError(clientErrorId: string): Promise<boolean> {
    return this._delete(clientErrorId, Secure.true).catch(async err => {
      return await this.handlePromiseError(
        'CustomErrorHandlerService',
        'deleteClientError',
        err
      );
    });
  }

  /**
   * Get the details of a client error record
   * @param clientErrorId - The ID of the client error to get
   * @returns A client error object
   * @async
   */
  public async getClientError(
    clientErrorId: string
  ): Promise<ClientErrorReport> {
    const clientError = await this._get<ClientErrorReport>(
      clientErrorId,
      Secure.true
    ).catch(async err => {
      return await this.handlePromiseError(
        'CustomErrorHandlerService',
        'getClientError',
        err
      );
    });
    return clientError;
  }

  /**
   * Handle a standard error
   * @param error - The error object
   * @async
   */
  public async handleError(error: any): Promise<null> {
    console.log('Default error handler caught an error');
    console.log(error);
    if (this.config.production === false) {
      await this.errorDialogService.openErrorDialog(
        'Unexpected Error',
        'An unexpected error occurred: ' + error.message
      );
    }
    const errorReport = new ClientErrorReport(
      'unknown',
      error.message,
      error.status,
      error.statusText,
      error.url
    );
    const clientErrorReport = await this.reportClientError(errorReport);
    document.location.href =
      this.config.errorRoute + '?errorReportId=' + clientErrorReport._id;
    return null;
  }

  /**
   * Report a client error
   * @param errorReport - The client error object
   * @returns The client error report
   * @async
   */
  public async reportClientError(
    errorReport: ClientErrorReport
  ): Promise<ClientErrorReport> {
    const error = await this._post<ClientErrorReport>(
      errorReport,
      Secure.true,
      false
    ).catch(async err => {
      return await this.handlePromiseError(
        'CustomErrorHandlerService',
        'reportHttpError',
        err
      );
    });
    return error;
  }
}
